<template>
    <b-container fluid class="d-flex flex-column mh-100 h-100 w-100" :style="generateVariantStyle(Variant)">
        <b-row class="flex-grow-1 w-100 overflow-auto no-gutters p-1 h-100 bg-gray-light">
            <splitpanes class="default-theme">
                <!-- Composants Work chargés dynamiquement -->
                <pane :size="100 - rightPan" class="p-0 d-flex flex-column pb-3">
                    <b-row v-if="featureSelectMode !== selectModeEnum.Single">
                        <b-col>
                            <!-- Liste des filtres en multi-nodes -->
                            <Pins :FilterTreeStore="filterTreeStore" :Variant="Variant" :PropertiesFilterPinTypes="propertiesFilterPinTypes" />
                        </b-col>
                    </b-row>

                    <!-- Sélecteur des composants Work -->
                    <b-row class="m-0">
                        <b-col cols="2" class="p-0">
                            <!-- 'Display.Title' -> "Affichage" -->
                            <b-dropdown v-if="worksItems && worksItems.length > 1" ref="dropdownWorks" dropright :text="$t('Display.Title')" variant="outline-gray-dark" toggle-class="border-0 rounded-pill h6" class="py-1">
                                <b-dropdown-form form-class="px-1">
                                    <!-- Liste des composants Works -->
                                    <b-form-checkbox-group v-model="selectedWorksItems" switches>
                                        <draggable :list="worksItems">
                                            <div v-for="workItem in worksItems" :key="workItem.Name" class="d-flex">
                                                <b-icon icon="grip-vertical" class="mt-1" />
                                                <label :for="'chkbx' + workItem.Name" class="mb-0">{{ workItem.Label }}</label>
                                                <b-form-checkbox :id="'chkbx' + workItem.Name"
                                                    :key="workItem.Name"
                                                    :value="workItem"
                                                    :aria-describedby="workItem.Label" :button-variant="Variant" class="ml-auto mr-0">
                                                </b-form-checkbox>
                                            </div>
                                        </draggable>
                                    </b-form-checkbox-group>
                                </b-dropdown-form>
                            </b-dropdown>
                        </b-col>
                        <b-col class="text-right p-0">
                            <b-row>
                                <!-- Titre du résultat-->
                                <b-col cols="11" class="text-right p-0 my-auto">
                                    <b-badge variant="gray" class="font-weight-normal my-auto mr-2 p-2 w-50 text-truncate align-middle" v-b-tooltip.hover :title="title">{{ title }}</b-badge>
                                </b-col>
                                <!-- Bouton téléchargement -->
                                <b-col cols="auto" class="px-0">
                                    <AnalysisExportWork
                                        :ExportWorkId="$id()"
                                        :DataSet="dataSet"
                                        :Configs="configs"
                                        :ExportTypesEnabled="metaData.ExportTypes" />
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>

                    <!-- Affichage des composants Work -->
                    <splitpanes horizontal class="default-theme flex-grow-1 w-100 overflow-auto no-gutters p-0" @resized="computeWorksHeight($event)">
                        <!-- Composants Work chargés dynamiquement -->
                        <pane :id="'spAnalysis' + workItem.Name" v-for="(workItem, index) in selectedWorksItems" :key="index"  min-size="25">
                            <b-row class="m-0 h-100 px-3 pt-0">
                                <b-col v-if="works[workItem.Name].Result || works[workItem.Name].Loading" class="p-1 h-100">
                                    <component :id="$id('ExportWork-' + workItem.Name)" v-bind:is="works[workItem.Name].Component" :Title="works[workItem.Name].Title"
                                            :Results="works[workItem.Name].Result"
                                            :Configs="configs"
                                            :Loading="works[workItem.Name].Loading"
                                            :MaxHeight="workItem.Height + 'px'" />
                                </b-col>
                                <b-col v-else class="p-5">
                                    <!-- 'Display.NoResults.Line1' -> "Appliquer un périmètre enregistré" -->
                                    <!-- 'Display.NoResults.Line2' -> "ajouter un périmètre filtré par propriété d'objet" -->
                                    <div class="h6 text-center">{{ $t('Display.NoResults.Line1') }} <b-icon icon="funnel" />, {{ $t('Display.NoResults.Line2') }} <b-icon icon="tags" />,</div>
                                    <!-- this.$t('Display.NoResults.Line3') -> "ou cliquer sur l'arborescence pour afficher les résultats." -->
                                    <div class="h6 text-center">{{ $t('Display.NoResults.Line3') }}</div>
                                </b-col>
                            </b-row>
                        </pane>
                    </splitpanes>
                </pane>

                <!-- Panneau de configuration -->
                <!-- L'event ChangeOverFlowEvent permet de modifier la propriété de style overflow selon l'êtat de la dropdown du composant Config -->
                <pane :size="rightPan" :min-size="rightPan" class="p-0 flex-shrink-0" :style="overflowState ? 'overflow:visible' : 'overflow:hidden'">
                    <Config ref="Config" :Configs.sync="configs" :Loading="loading" :Variant="Variant" :SelectMode="featureSelectMode"
                        @LoadWorkResultEvent="loadWorksResult()" @ChangeOverflowEvent="changeOverflow" />
                </pane>
            </splitpanes>
        </b-row>
    </b-container>
</template>

<script lang="ts">
    import { Component, Prop, ProvideReactive, Vue, Watch} from "vue-property-decorator";
    import { mixins } from 'vue-class-component';
    import { Splitpanes, Pane } from 'splitpanes';
    import { Getter } from "vuex-class";
    import Dictionary from "@/types/Dictionary";
    import axios from "axios";
    import draggable from 'vuedraggable';

    import WorkLibrary from "@/types/WorkLibrary";

    /** Arborescence principale */
    import { API_URL, MAIN_FILTER_TREE } from "@/App.settings";

    /** Panneau du milieu */
    import Pins from "@/components/commons/Pins.vue";
    /** Liste des composants Work affichés dans le panneau du milieu */
    import ChartWork from "@/components/commons/Work/ChartWork.vue";
    import GridWork from "@/components/commons/Work/GridWork.vue";
    import OfficialLabelsWork from "@/components/commons/Work/OfficialLabelsWork.vue";

    /** Export de composants Work */
    import AnalysisExportWork from "@/components/commons/Work/AnalysisExportWork.vue";

	/** Largeur par défaut de la partie de droite */
	export const DEFAULT_RIGHT_PAN = 22;

    /** Panneau de configuration de droite */
    import Config from '@/components/commons/Config.vue';
    import { ThemeUtils } from "@/components/commons/Utils";
    import FilterT from "@/types/FilterTree/FilterT";

    import SelectModeEnum from "@/enums/SelectModeEnum";
    import locales from "./locales/Analysis.json"
    import AnalysisMetaDataAttribute from "@/types/AnalysisMetaDataAttribute";
import PinTypeEnum from "@/enums/PinTypeEnum";


    /** Composant spécifique pour la gestion d'Analysis */
    @Component({
        components: {
            Splitpanes, Pane,
            draggable,

            /** Panneau de fonction */
            Config,
            /** Pins de filtrage sur l'arborescence */
            Pins,
            
            /** Liste des composants Work disponibles */
            ChartWork,
            GridWork,
            OfficialLabelsWork,

            /** Export de composants Work */
            AnalysisExportWork
        },
        i18n: { messages: locales }
    })
    export default class Analysis extends mixins(ThemeUtils) {
        /** Pour utilisation de l'enum dans la section template */
        private selectModeEnum = SelectModeEnum;

		/** Variant de couleur à appliquer sur le composant */
		@Prop({default: () => "secondary"})
		public Variant!: string;

		/** Context du composant parent : Widget, WidgetConfig ou Analysis */
		@ProvideReactive()
		private parentContext: string="Analysis";

        /**  Informe les composants enfants le nom de l'instance d'arborescence à utiliser */
        @ProvideReactive()
        private filterTreeStore: string = MAIN_FILTER_TREE;

        /** Taille de la partie de droite de sélection de fonction et de paramétrage */
		private rightPan: number = DEFAULT_RIGHT_PAN;

		/** Contient l'ensemble des configs à envoyer au server sous forme de 3 clés : Features, Work, Heritage */
		private configs: Dictionary<any> = { };

        //#region Pour export des données (composant AnalsisExportWork.vue)
        /** Pour export */
        private dataSet: any = {};

        /** Meta-données définies via l'attribut AnalysisAttribute sur la fonction Analysis en cours d'utilisation */
        private metaData: AnalysisMetaDataAttribute = new AnalysisMetaDataAttribute();
        //#endregion Pour export des données (composant AnalysisExportWork.vue)

        /** Titre affichant les paramètres qui sera généré à partir de la sélection dans le panneau de config  */
        private title: string = "";

        /** Permet de changer l'overflow pour eviter que les dropdown dans le composant Config soient tronquées */
        private overflowState: boolean = false;

        /**
         * Mise à jour du booléen d'êtat d'overflow selon l'êtat de la dropdown de Config afin qu'il ne soit pas tronqué
         * @param state Etat de la dropdown (show / hide)
         */
        private changeOverflow(state: boolean){
            this.overflowState = state;
        }

        //#region Paramétrage composant Work
		/** Indique si le composant Work doit afficher un overlay de chargement ou non */
		private loading: boolean = false;

        /** Liste "allégée" des Works utilisée pour le sélecteur de Works */
        private selectedWorksItems: WorkItem[] = [];
        /** Liste "allégée" de Works sélectionnée dans le sélecteur de Works */
        private worksItems: WorkItem[] = [];

        /** Liste des composants Works affichés */
        private works: Dictionary<WorkLibrary> = {};

        /** Liste des types de Pin pour les filtres par propriétés */
        private propertiesFilterPinTypes: PinTypeEnum[] = [ PinTypeEnum.None, PinTypeEnum.Tree];

        /** Mise à jour de l'ordre des WorksItems du sélecteur de Works */
        @Watch('worksItems')
        private updateWorkItemsOrder() {
            console.debug("Analysis - updateWorkItemsOrder() 1 -  this.worksItems : %O - this.selectedWorksItems : %O",  this.worksItems, this.selectedWorksItems);

            let tempSelectedWorks: WorkItem[] = [];

            this.worksItems.forEach(w => {
                if (this.selectedWorksItems.includes(w))
                    tempSelectedWorks.push(w);
            });
            
            this.selectedWorksItems = tempSelectedWorks;
            console.debug("Analysis - updateWorkItemsOrder() 2 -  this.worksItems : %O - this.selectedWorksItems : %O",  this.worksItems, this.selectedWorksItems);
        }

        /** 
         * Recalcul la hauteur des composants Works en fonction de la taille de la Pane dans laquelle ils sont placés
         * @param e Event passé par l'appelant
         */
        private computeWorksHeight(e) {
            console.debug("Analysis - computeWorksHeight(e) - e : ", e);

            /** Boucle sur la liste des Work pour recalculer leur hauteur à partir de la hauteur de chaque Pan du SplitePane */
            for(let workItem of this.selectedWorksItems) {
                var element = document.getElementById('spAnalysis' + workItem.Name); // (this.$refs.GlobalFilter as HTMLElement);
                if (element === null)
                    continue;
                    
                console.debug("Analysis - log() - element.scrollHeight : %f", element.scrollHeight);
                console.debug("Analysis - log() - element.offsetHeight : %f", element.offsetHeight);

                //si un seul work , additionner scrollHeight + offsetHeight sinon uniquement offsetHeight
                let height = this.selectedWorksItems.length == 1 ? element.scrollHeight + element.offsetHeight : element.offsetHeight;
                workItem.Height = height - 50;
            }
        }

        /** Charge le result dans les différents composants Work */
        private async loadWorksResult() {
            console.debug("Analysis - loadWorksResult() - this.configs : ", this.configs);

            /** Affiche le chargement de chaque Work */
            for(let index in this.works)
                Vue.set(this.works[index], 'Loading', true);

            this.loading = true;

            let grouping: FilterT = this.$store.getters[MAIN_FILTER_TREE + "/GetGrouping"];
            this.configs["Grouping"] = grouping ?? {};

            console.debug("Analysis - loadWorksResult() - grouping : ", grouping);

            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["ParametersValues"] = JSON.stringify(this.configs);

            await axios.post(API_URL + "Analysis/" + this.featureName + "/LoadResults", args).then((response) => {
                 if (response && response.data) {
                     let results: Dictionary<any> = response.data;

                    console.debug("Analysis - loadWorksResult() - results : ", results);

                    /** Boucle sur les différents Works pour affecter le résultat obtenu du server */
                    for(let index in this.works) {
                        console.debug("Analysis - loadWorksResult() - work : ", this.works[index]);
                        if (this.works[index].Name in results) {
                             console.debug("Analysis - loadWorksResult() - work.Name : " + this.works[index].Name + " - results", results[this.works[index].Name])
                             Vue.set(this.works[index], 'Result', results[this.works[index].Name]);
                             Vue.set(this.works[index], 'Title', this.title);
                             Vue.set(this.works[index], 'Loading', false);
                        }
                    }

                    // DataSet d'export de données
                    Vue.set(this, "dataSet", results["DataSet"]);

                    // Meta-données définies via l'attribut AnalysisAttribute sur la fonction Analysis en cours d'utilisation
                    if ("MetaData" in results)
                        Vue.set(this, "metaData", results["MetaData"]);

                    // Applique les Parameters mis à jour côté server
                    Vue.set(this, "configs", results["Parameters"]);
                    console.debug('Analysis - $refs["Config"]', this.$refs["Config"]);

                    /** Mise à jour du titre à partir de la méthode implémentée dans Config.vue qui génère le titre selon la fonction Analysis chargée */
                    this.title = (this.$refs["Config"] as any).GenerateTitle();
                 }
            })
            .catch(err => {
                /** Désactivation des loading */
                for(let index in this.works) {
                    console.debug("Analysis - loadWorksResult() - work : ", this.works[index]);
                    Vue.set(this.works[index], 'Loading', false);
                }

                console.error(err);
                // 'AnErrorOccuredContactAdmin' -> "Une erreur est survenue. Veuillez contacter votre administrateur."
                //  Error -> "Erreur"
                this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                     title: this.$t('Error').toString(),
                     autoHideDelay: 10000,
                     variant: "danger",
                     solid: true,
                     appendToast: true,
                     toaster: "b-toaster-top-center"
                });
            })
            .finally(() => { 
                this.loading = false;
            });
		}
        //#endregion Paramétrage composant Work

        //#region Gestion panneau de Features (Config)
        @Getter('GetFeatureName')
		private featureName!: string;

        /** Mode de sélection de la fonction courante */
		private featureSelectMode!: SelectModeEnum;

        /** Mise à jour du panneau de Feature avec ses Parameters ainsi que les Work associés (déclaré avec l'attribut Analysis en meta-données d'un contrôleur héritant d'IAnalysisController') */
        @Watch('featureName', { immediate: true })
        private async updateFeature() {
            console.debug("Analysis - updateFeature - featureName : %O - GetFeatureTarget : %O", this.featureName, this.$store.getters["GetFeatureTarget"]);

            if (this.featureName && this.$store.getters["GetFeatureTarget"] === "Analysis") {
                // Initialisation des meta-données de la feature
                this.metaData = new AnalysisMetaDataAttribute();

                //#region Chargement de la liste des composants Work utilisables
                /** Chargement de la liste des composants Work utilisables */
                /** @todo Voir avec SLavina si nécessaire de charger tous les works en une seule fois ou bien à chaque load ? */
                let args: Dictionary<any> = {};
                args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
                args["Feature"] = this.featureName;

                await axios.post<Dictionary<WorkLibrary>>(API_URL + "Analysis/GetWorksLibrary", args).then(async (response) => {
                    if (response && response.data) {
                        console.debug("Analysis - updateFeature() - Analysis/GetWorksLibrary - response.data : ", response.data);

                        /** Liste des Works effective passé à tous visibles par défaut */
                        this.works = response.data;

                        /** Constitue une liste des Works manipulable pour le sélecteur de Works */
                        this.selectedWorksItems = Object.values(this.works).map(x => ({ Name: x.Name, Label: x.Title, Height: undefined }));
                        this.worksItems = this.selectedWorksItems;

                        console.debug("Analysis - updateFeature() - this.works : ", this.works);
                    }
                })
                .catch(err => {
                    console.error(err);
                    // 'AnErrorOccuredContactAdmin' -> "Une erreur est survenue. Veuillez contacter votre administrateur."
                    //  Error -> "Erreur"
                    this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                });
                //#endregion Chargement de la liste des composants Work utilisables

                //#region Chargement des paramètres par défaut
                /** Chargement de la liste des Parameters pour la fonction */
                args = {};
                args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

                await axios.post(API_URL + "Analysis/" + this.featureName + "/LoadParameters", args).then((response) => {
                    if (response && response.data) {
                        Vue.set(this, "configs", response.data);
                        /** Met à jour Configs avec les Pins déjà cochés précédemment */
                        // console.debug("Analysis - newPins : ", FilterTreeUtils.SetPinTree(this.$store.getters[this.filterTreeStore + '/GetSelectedNode'], false));
                        
                        // switch(this.featureSelectMode) {
                        //     case SelectModeEnum.Single:
                        //         Vue.set(this.configs, "Pins", FilterTreeUtils.SetPinTree(this.$store.getters[this.filterTreeStore + '/GetSelectedNode'], false));
                        //         break;
                        //     case SelectModeEnum.MultiAdvanced:
                        //     default:
                        //         Vue.set(this.configs, "Pins", this.$store.getters[this.filterTreeStore + '/GetPins']);
                        //         break;
                        // }

                        console.debug("Analysis - updateFeature() - pins : ", this.$store.getters[MAIN_FILTER_TREE + "/GetPins"]);

                        Vue.set(this.configs, "Pins", this.$store.getters[MAIN_FILTER_TREE + "/GetPins"]);

                        console.debug("Analysis - updateFeature() - this.configs : ", this.configs);

                        this.title = "";
                    }
                })
                .catch(err => {
                    console.error(err);
                    // 'AnErrorOccuredContactAdmin' -> "Une erreur est survenue. Veuillez contacter votre administrateur."
                    //  Error -> "Erreur"
                    this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                });
                //#endregion Chargement des paramètres par défaut
            }
        }
        //#endregion Gestion panneau de Features (Config)

        /** 
         * Pointeur vers fonction pour désactiver watcher sur l'instance de FilterTree () sur le noeud ou les pins sélectionnés 
         * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher
         **/
        private unwatchFilterTree: () => void = undefined;

        created() {
            this.unwatchFilterTree = this.$store.watch((state, getters) => getters[this.filterTreeStore + '/GetSelectMode'], val => {
                this.featureSelectMode = val;
            }, { immediate: true});
        }

        beforeDestroy () {
            if(this.unwatchFilterTree)
                this.unwatchFilterTree();
        }
    }

    /** Item de Work utilisé pour l'affichage dans le selecteur de composant Work */
    type WorkItem = {
        /** Nom */
        Name: string; 
        /** Libellé */
        Label: string; 
        /** Hauteur de composant Work */
        Height: number;
    }
</script>

<style scoped>
    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
            border-color: var(--Variant) !important;
            background-color: var(--Variant) !important;
        }

    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
    :deep(.custom-control-input:active ~ .custom-control-label::before) {
        background-color: var(--Variant) !important;
        border-color: var(--Variant) !important;
    }
</style>